<script lang="ts">
    import {getLogger} from 'src/core';
    import type {TogglePopoverFn} from 'shared/ui-widgets/popover';
    import {MarkdownRichTextArea} from 'shared/ui-widgets/markdown-richtext-area';
    import type {FormControlOptions} from '../../forms/types';
    import type {MarkdownValueEditorLocalizations, MarkdownValueEditorOptions} from './types';
    import type {TextValueEditorValidations} from '../text/types';
    import type {EditorsLocalizationFunction} from '../../../localizations/types';
    import type {FormControl} from '../../forms/form-control';
    import type {ValueEditorSize} from '../../../types';
    import {FormControlBuilder} from '../../forms/form-control';
    import {emptyAsNullFormatter, emptyAsNullParser} from '../_shared/editors-utils';
    import {createInputDescription} from '../../description/utils';
    import {getContext, onDestroy, onMount, tick} from 'svelte';
    import {markdownFormatter, markdownParser, validatorFactory} from './utils';
    import {touched} from '../../shared/use.touched';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import {style} from 'svelte-forms';
    import {ariaLabelledby} from '../../label/use.aria-labelledby';
    import {getValueEditorLabelContext} from '../../label/value-editor-label-context';
    import {disabledAttribute} from '../../shared/use.disabled-attribute';
    import {cleanup, ignoreUnusedProperties, isFunction, uuid} from 'shared/utils/custom-utils';
    import {createFloatingActions} from 'svelte-floating-ui';
    import {floatingUiCommonSettings} from 'shared/utils/floating-ui-common-settings';
    import removeMd from 'remove-markdown';
    import {EDITORS_LOCALIZE} from '../../../context-keys';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import Description from '../../description/Description.svelte';
    import KpPopoverPanel from '../../../../ui-widgets/popover/KpPopoverPanel.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';

    export let model: string;
    export let formControlOptions: FormControlOptions;

    export let editorId: string;
    export let editorName: string;
    export let placeholder: string;
    ignoreUnusedProperties(placeholder); // markdown editor does not support placeholder

    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;

    export let options: MarkdownValueEditorOptions;
    export let validations: TextValueEditorValidations;

    const localize: EditorsLocalizationFunction<MarkdownValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);
    const labelContext = getValueEditorLabelContext();

    const formControl = FormControlBuilder.for(editorId, editorName, model)
        .withValidators(validatorFactory(validations))
        .withOptions(formControlOptions)
        .withParsers([markdownParser()])
        .withModifiers([emptyAsNullParser(options)])
        .withFormatters([markdownFormatter(), emptyAsNullFormatter(options)])
        .build(getLogger());

    let editorFocusFn: () => void;

    const modelValueSubscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(model); // two-way data binding -> receiving new model value from parent

    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});

    onMount(async () => {
        if (!options.inline && isFocused && !isDisabled) {
            if (isFunction(editorFocusFn)) {
                editorFocusFn();
            }
        }

        await tick(); // wait for the popover to render
        if (options.inline && !isDisabled && isFunction(togglePopover)) {
            togglePopover(true);
        }
    });

    onDestroy(() => {
        cleanup(modelValueSubscription);
    });

    // Popover stuff for inline mode
    const popoverId = uuid();
    let togglePopover: TogglePopoverFn;
    const [floatingRef, floatingContent] = createFloatingActions(floatingUiCommonSettings('bottom-end'));

    export function getFormController(): FormControl<string> {
        return formControl;
    }

    const handleMarkdownUpdated = (event: CustomEvent<string>) => {
        formControl.setViewValue(event.detail);
    };

    const handlePopoverOpen = async () => {
        await tick(); // wait for the editor to render
        if (isFunction(editorFocusFn)) {
            editorFocusFn();
        }
    }
</script>

<div class="form-control input-{size} richtext-area-wrapper"
     class:inline-mode={options.inline}
     id={editorId}
     data-name={editorName}

     use:floatingRef
     use:disabledAttribute={isDisabled}
     use:touched={{formControl, touchTriggerEvent: 'focusout'}}
     use:style={{field: formControl.getFieldStateAsStore()}}
     use:ariaInvalid={formControl}
     use:ariaErrormessage={formControl}
     use:ariaDescribedby={inputDescription}
     use:ariaLabelledby={labelContext}
     data-main-input>

    {#if options.inline}
        {@const removedMd = removeMd(model)}

        <button
                type="button"
                class="input-{size} markdown-label"
                disabled={isDisabled}
                popovertarget={popoverId}
        >
            {#if !removedMd}
                <span class="value-container text-muted">{localize('emptyValue')}</span>
            {:else}
                <div class="value-container">{removedMd}</div>
            {/if}

            <KpButton buttonSize={size} tooltipLabel="Upravit formátovaný text">
                <IconedContent icon="edit">
                    <span class="sr-only">{localize('editValue')}</span>
                </IconedContent>
            </KpButton>
        </button>
    {:else}
        <MarkdownRichTextArea on:markdown-updated={handleMarkdownUpdated}
                              value={model}
                              disabled={isDisabled}
                              bind:focus={editorFocusFn}/>
    {/if}
</div>

{#if options.inline}
    <KpPopoverPanel
            id={popoverId}
            use={[floatingContent]}
            additionalClasses="markdown-editor-popover"
            bind:togglePopover
            on:popover-close={() => formControl.setTouched()}
            on:popover-open={handlePopoverOpen}
    >
        <MarkdownRichTextArea on:markdown-updated={handleMarkdownUpdated}
                              value={model}
                              options={{inPopoverMode: true}}
                              disabled={isDisabled}
                              bind:focus={editorFocusFn}/>
    </KpPopoverPanel>
{/if}

<ErrorMessages formController={formControl} {forceShowErrors} {size}/>
<Description {inputDescription}/>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    .form-control.richtext-area-wrapper {
        padding: 0;
        height: auto;
        min-height: 500px;
        background-color: transparent;
        border: none;
        position: relative;
        border-radius: 0;
        box-shadow: none;
        display: flex;
        flex-direction: column;

        &.inline-mode {
            width: 100%;
            min-height: auto;
        }

        :global {
            .richtext-area {
                flex: 1 1 0;
            }
        }
    }

    .markdown-label {
        width: 100%;
        display: flex;
        align-items: center;
        gap: @spacing-xs;
        outline: none;
        border: none;
        cursor: pointer;
        border-radius: @border-radius-default;
        background-color: transparent;
        transition: background-color 0.3s ease-in-out;

        &:hover {
            background-color: @themed-body-bg-blue-highlighted;
        }

        .value-container {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    :global {
        .markdown-editor-popover {
            --popover-content-padding: 0;
        }
    }
</style>